import classNames from 'classnames'
import i18next from 'i18next'
import * as React from 'react'
import type { PhotoFile } from '../../../shared/domain/flows/types.no-deps'
import type { CommonDeps } from '../base/CommonDeps'
import { renderCatch } from '../error/renderCatch'
import { type FileUrlStates, withFileUrl } from '../file/FileUrlManager'
import { AnnotationPage } from '../photo/AnnotationPage'
import { SVG_Delete } from '../svg/SVG_Delete'
import { SVG_Exit } from '../svg/SVG_Exit'

interface Props {
    deps: CommonDeps
    file: PhotoFile
    deletePhoto: () => Promise<void>
    saveAnnotation: (annotation: Blob) => Promise<void>
    exitPreview: () => void
    fileUrlStates: FileUrlStates
    isIOS?: boolean
}

interface State {
    showDeleteConfirmation: boolean
    showAnnotationPage: boolean
}

export class PhotoPreview extends React.Component<Props, State> {
    public state: State = {
        showDeleteConfirmation: false,
        showAnnotationPage: false,
    }

    public render = () => renderCatch(this.props.deps, this.tryRender)

    private saveAnnotation = async (annotation: Blob) => {
        await this.props.saveAnnotation(annotation)
        this.setState({ showAnnotationPage: false })
    }

    private showPhotoDeletionModal = () => {
        const heading = i18next.t('appcore:PhotoPreview.promptDelete.heading')
        const message = i18next.t('appcore:PhotoPreview.promptDelete.message')
        const continueText = i18next.t('appcore:PhotoPreview.promptDelete.confirm')
        const cancelText = i18next.t('appcore:common.cancel')

        return (
            <zaptic-modal z-index={4}>
                <zaptic-full-page-message heading={heading}>
                    <div>{message}</div>
                    <zaptic-spacer vertical-spacing={35}></zaptic-spacer>
                    <zaptic-popup-button color="red" onClick={this.props.deletePhoto}>
                        {continueText}
                    </zaptic-popup-button>
                    <zaptic-popup-button
                        color="white"
                        onClick={() => {
                            this.setState({ showDeleteConfirmation: false })
                        }}
                    >
                        {cancelText}
                    </zaptic-popup-button>
                </zaptic-full-page-message>
            </zaptic-modal>
        )
    }

    private exitPreview = (event: React.MouseEvent<HTMLDivElement>) => {
        event.stopPropagation()
        this.props.exitPreview()
    }

    private previewTopBar = () => {
        return (
            <div className="AppCore__flow__Preview-top-bar">
                <div className="AppCore__flow__Preview-exit" onClick={this.exitPreview}>
                    <SVG_Exit color={'var(--white)'} width={32} height={32} />
                </div>
            </div>
        )
    }

    private previewImage = () => {
        return (
            <div className="AppCore__flow__Preview-image-wrapper">
                {withFileUrl(this.props.deps, this.props.fileUrlStates, this.props.file.photo, (url) =>
                    withFileUrl(this.props.deps, this.props.fileUrlStates, this.props.file.annotation, (annotation) => (
                        <zaptic-annotated-image src={url} annotation={annotation}></zaptic-annotated-image>
                    ))
                )}
            </div>
        )
    }

    private previewBottomBar = () => {
        return (
            <div className="AppCore__flow__Preview-bottom-bar">
                <div
                    className="AppCore__flow__Preview-delete"
                    onClick={() => this.setState({ showDeleteConfirmation: true })}
                >
                    <SVG_Delete color={'var(--white)'} />

                    <span className="AppCore__flow__Preview-delete-text">{i18next.t('appcore:common.delete')}</span>
                </div>
                <div
                    className="AppCore__flow__Preview-annotate"
                    data-testid="annotate-button"
                    onClick={() => this.setState({ showAnnotationPage: true })}
                >
                    <zaptic-icon icon-name="annotate"></zaptic-icon>

                    <span className="AppCore__flow__Preview-annotate-text">
                        {i18next.t('appcore:PhotoPreview.annotate')}
                    </span>
                </div>
            </div>
        )
    }

    private tryRender = () => {
        return (
            <zaptic-modal z-index={4}>
                {this.state.showAnnotationPage ? this.renderAnnotationPage() : this.renderPreview()}
            </zaptic-modal>
        )
    }

    private renderAnnotationPage() {
        const { file, fileUrlStates } = this.props
        const photoState = fileUrlStates.get(file.photo)
        const annotationState = file.annotation != null ? fileUrlStates.get(file.annotation) : null

        return (
            <AnnotationPage
                deps={this.props.deps}
                photoState={photoState}
                annotationState={annotationState ?? { status: 'resolved', value: null }}
                onCancel={() => this.setState({ showAnnotationPage: false })}
                onSave={this.saveAnnotation}
            />
        )
    }

    private renderPreview() {
        return (
            <div
                className={classNames('AppCore__flow__Preview-wrapper', {
                    'AppCore__flow__Preview-wrapper--ios': this.props.isIOS,
                })}
            >
                {this.previewTopBar()}
                {this.previewImage()}
                {this.previewBottomBar()}
                {this.state.showDeleteConfirmation && this.showPhotoDeletionModal()}
            </div>
        )
    }
}
